/**
 * Hook for tracking pending friend requests count
 */

import { useState, useEffect, useCallback } from 'react';
import { getPendingRequests } from '@/services/friends';
import { useAuthStore } from '@/stores/authStore';

interface UsePendingFriendRequestsResult {
  count: number;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Custom event name for friend request changes
 * Dispatch this event to trigger a refresh of the pending count
 */
export const FRIEND_REQUESTS_CHANGED_EVENT = 'friend-requests-changed';

/**
 * Dispatch this function to notify all usePendingFriendRequests hooks to refresh
 */
export function notifyFriendRequestsChanged(): void {
  window.dispatchEvent(new CustomEvent(FRIEND_REQUESTS_CHANGED_EVENT));
}

/**
 * Hook to track the number of pending incoming friend requests
 *
 * @param pollInterval - How often to refresh the count in ms (0 to disable)
 * @returns Object with count, loading state, and refresh function
 */
export function usePendingFriendRequests(
  pollInterval: number = 60000
): UsePendingFriendRequestsResult {
  const { isAuthenticated } = useAuthStore();
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setCount(0);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getPendingRequests();
      if (!response.error && response.data) {
        setCount(response.data.length);
      }
    } catch (error) {
      console.error('Failed to fetch pending friend requests:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Initial fetch
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Polling
  useEffect(() => {
    if (!isAuthenticated || pollInterval <= 0) {
      return;
    }

    const intervalId = setInterval(refresh, pollInterval);
    return () => clearInterval(intervalId);
  }, [isAuthenticated, pollInterval, refresh]);

  // Listen for friend request changes from other components
  useEffect(() => {
    const handleChange = () => {
      refresh();
    };

    window.addEventListener(FRIEND_REQUESTS_CHANGED_EVENT, handleChange);
    return () => {
      window.removeEventListener(FRIEND_REQUESTS_CHANGED_EVENT, handleChange);
    };
  }, [refresh]);

  return { count, isLoading, refresh };
}
